import React from "react";

import { IChoiceField } from "../models/formFields.interfaces";
import { IFormFieldState } from "./AbstractFormField";
import { AbstractSelect } from "./AbstractSelect";

const Option = (props: { label: string; value: string }): React.JSX.Element => (
    <option value={props.value}>{props.label}</option>
);

// TODO(ssr, LEVEL 5 out of 10): Migrate to functional component
// Complexity: Dropdown select element with componentDidMount lifecycle containing IE Edge bug
// workaround (manipulates selectedIndex DOM directly, dated Sept 2016). Maps choices to Option
// components. Calls super.componentDidMount(). The IE Edge fix temporarily changes selected option
// to force proper rendering, which is complex DOM manipulation that needs careful migration.
// Migration: Use useFormField hook (from migrated AbstractFormField) for base functionality. Replace
// componentDidMount with useEffect that runs once on mount. The IE Edge workaround logic should be
// preserved as-is inside useEffect (may still be needed for legacy browser support). Map choices to
// render options. Replace inputElem ref with useRef. Main complexity is preserving the IE Edge fix.
export class FormSelect<T extends string> extends AbstractSelect<
    T,
    IChoiceField<T>,
    IFormFieldState
> {
    public static defaultProps = {
        type: "choice",
    };

    public state: IFormFieldState = {
        focused: false,
    };

    /**
     * This exists strictly as an IE Edge bug workaround. Done on 10 Sept 2016
     * See https://github.com/facebook/react/issues/7655 and
     * https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8794503/
     * It fixes a bug where the Wells Fargo application wasn't taking the State value
     * into its select box, and it was throwing a validation error.
     */
    public componentDidMount() {
        let selectedIndex = -1;
        const input = this.getInputElem();
        if (input) {
            if (
                typeof input !== "undefined" &&
                typeof input.selectedIndex !== "undefined"
            ) {
                selectedIndex = input.selectedIndex;
            }

            if (selectedIndex >= 0) {
                let tempOption: HTMLOptionElement | null = null;
                if (input.childNodes.length === 1) {
                    tempOption = document.createElement("option");
                    input.appendChild(tempOption);
                }

                const options = input.options;
                const tempIndex = (selectedIndex + 1) % options.length;
                options.item(tempIndex)!.selected = true;
                options.item(selectedIndex)!.selected = true;

                if (tempOption) {
                    input.removeChild(tempOption);
                }
            }
        }

        super.componentDidMount();
    }

    protected buildControl(): React.JSX.Element {
        const props = this.getInputProps();

        const options = (this.props.choices || []).map((option) => (
            <Option key={option.value} {...option} />
        ));

        props.id ??= this.props.id;
        return (
            <select
                ref={(ref) => {
                    this.inputElem = ref;
                }}
                {...props}
            >
                {options}
            </select>
        );
    }
}
